import { useState } from "react";
import { DateTime } from "luxon";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./PhoneCallNoticePopup.module.scss";

interface IPhoneCallNoticePopupProps {
    onClose?: () => void;
    onOk?: (date: Date) => void;
}

function PhoneCallNoticePopup(props: IPhoneCallNoticePopupProps): JSX.Element {
    const today = new Date();
    const { getText } = useTranslation();
    const [date, setDate] = useState(today);
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    function renderFooter(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="flex-container flex-end">
                    <ButtonControl
                        label={getText("CancelLabel")}
                        className="btn btn-default margin-sm-left"
                        onClick={() => props.onClose?.()}
                    />
                    <ButtonControl
                        label={getText("Ok_Label")}
                        className="btn btn-primary margin-sm-left"
                        onClick={() => props.onOk?.(date)}
                        disabled={!date}
                    />
                </div>
            </div>
        );
    }

    return (
        <Modal
            showX
            centerContent
            draggable
            title={getText("RecallList_PreventiveCareNoticeType_PhoneCall")}
            width={370}
            maxHeight="250px"
            top={1}
            showOverlay
            onClose={() => props.onClose?.()}
            footer={renderFooter()}
        >
            <div className={`${styles.content} margin-xs-top`}>
                <p>{getText("RecallList_PhoneCallNoticePopupDescription")}</p>
                <div className={styles.dateContainer}>
                    <p className={`${styles.label} bold`}>{getText("DateLabel")}</p>
                    <div>
                        <DateSelector
                            toDayEnabled
                            id="startDate"
                            valid={!!date}
                            date={date}
                            minDate={sixMonthsAgo}
                            maxDate={today}
                            onChangeCallback={value => {
                                const selectedDate = DateTime.fromJSDate(value).toLocal();
                                const today = DateTime.local();
                                let updatedDate: DateTime;

                                if (selectedDate.hasSame(today, "day")) {
                                    const { hour, minute, second, millisecond } = today;
                                    updatedDate = selectedDate.set({ hour, minute, second, millisecond });
                                }
                                else {
                                    updatedDate = selectedDate.startOf("day");
                                }

                                setDate(updatedDate.toJSDate());
                            }}
                            dateFormat="yyyy/MM/dd"
                            ignoreInvalidCharacters
                            width={150}
                        />
                        {!date && (
                            <div className="qr invalid">
                                <p className="text-danger">{getText("PreventiveCare_PhoneCallNotice_DateIsRequiredLabel")}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export { PhoneCallNoticePopup };
